import React, { useCallback, useEffect, useReducer, useState } from "react";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import isEqual from "lodash/isEqual";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import OverlayPage from "components/OverlayPage";
import { useClickBlockerContext } from "components/BlockableClickContext";
import UnsavedChangesDialog from "components/UnsavedChangesDialog";
import Loader from "components/Loader";
import StallApplicationHeader from "./StallApplicationHeader";
import StallApplicationControlPanel from "./StallApplicationControlPanel";
import StallApplicationFooter from "./StallApplicationFooter";
import { IPreferences } from "./ReviewFormPopup/ReviewFormPopup";
import StableEntry from "./StableEntry";
import ReviewFormPopup from "./ReviewFormPopup";
import {
  getReviewFormPopupData,
  getReviewValues,
  reducer,
  setStableEntries
} from "./helper";
import { useRacehorse360Api } from "hooks/api";
import { setStallApplicationUnsavedChangesPopupState } from "store/actions/racingSecretaryStallApplication";
import { useRSStallApplicationReduxStore } from "../useRSStallApplicationReduxStore";
import {
  APPLICATION_FORM_SUMMARY_REQUEST_KEY,
  LIST_STALL_APPLICATION_REQUEST_KEY
} from "../StallApplicationFormViewPage";
import StallApplicationReview from "components/StallApplicationReview";
import useStyles from "./styles";

interface IProps {
  stallApplicationForm: racehorse360.IStallApplicationForm;
  stallApplicationId: string;
  onClose: () => void;
}

const STALL_APPLICATION_REVIEW_TITLE = "Application";

const StallApplication = (props: IProps) => {
  const { stallApplicationForm, stallApplicationId, onClose } = props;

  const initialState = {
    stableEntries: [],
    shouldDisplayPPs: true,
    shouldDisplayApp: false
  };

  if (!stallApplicationId) return;

  const classes = useStyles();
  const { setBlockClick, setBlockClickCallback } = useClickBlockerContext();
  const queryClient = useQueryClient();
  const dispatchToRedux = useDispatch();
  const {
    useGetStallApplication,
    useListStableEntries,
    useSaveDraftStallApplication,
    useCompleteStallApplication
  } = useRacehorse360Api();

  const [state, dispatch] = useReducer(reducer, initialState);
  const [isReviewFormPopupOpen, setIsReviewFormPopupOpen] = useState(false);
  const {
    isRSStallApplicationUnsavedChangesPopupOpen,
    redirectToLinkCandidate,
    clearLinkCandidate
  } = useRSStallApplicationReduxStore();

  const { data: stallApplication, isLoading: isStallApplicationLoading } =
    useGetStallApplication(
      {
        id: stallApplicationId,
        getOptions: {
          select: [
            "arrivalDate",
            "sleepingRoomsNumber",
            "stallApplicationFormId",
            "status",
            "contactFullName",
            "stableEntriesCount",
            "trainer.firstName",
            "trainer.lastName",
            "firstChoiceFacility.code",
            "secondChoiceFacility.code",
            "contactPhone",
            "contactEmail",
            "contactZipCode",
            "contactState",
            "contactCity",
            "contactStreetAddress",
            "contactStreetAddressOptional",
            "carrierName",
            "policyNumber",
            "policyExpirationDate",
            "carrierZipCode",
            "carrierState",
            "carrierCity",
            "carrierStreetAddress",
            "carrierStreetAddressOptional",
            "isStablePlannedToSplit",
            "comment"
          ]
        }
      },
      {
        enabled: Boolean(stallApplicationId)
      }
    );

  const { data: stableEntriesData, isFetching: isListStableEntriesFetching } =
    useListStableEntries(
      {
        query: {
          stallApplicationIds: [stallApplicationId]
        },
        getOptions: {
          select: [
            "horse.name",
            "horse.lastTenPastPerformances",
            "customHorseName",
            "customHorseRegistrationNumber",
            "status",
            "raceReadyDate"
          ]
        }
      },
      {
        enabled: Boolean(stallApplicationId),
        onSuccess: data => {
          dispatch(setStableEntries(data.stableEntries || []));
        }
      }
    );
  const stableEntries = stableEntriesData?.stableEntries || [];

  const {
    isPending: isStallApplicationSaving,
    mutateAsync: saveAndCloseStallApplication
  } = useSaveDraftStallApplication();

  const {
    isPending: isStallApplicationCompleting,
    mutateAsync: completeStallApplication
  } = useCompleteStallApplication();

  const lockNavigation = () => {
    setBlockClick(true);
    setBlockClickCallback(() => handleOpenUnsavedChangesPopup);
  };

  const unLockNavigation = () => {
    setBlockClick(false);
    setBlockClickCallback(null);
  };

  const areAllEntriesApproved =
    Boolean(state.stableEntries?.length) &&
    state.stableEntries.every(
      item =>
        item.status ===
        racehorse360.StableEntryStatus.STABLE_ENTRY_STATUS_APPROVED
    );
  const areAllEntriesDenied =
    Boolean(state.stableEntries?.length) &&
    state.stableEntries.every(
      item =>
        item.status ===
        racehorse360.StableEntryStatus.STABLE_ENTRY_STATUS_DENIED
    );
  const areAllEntriesSelected =
    Boolean(state.stableEntries?.length) &&
    state.stableEntries.every(
      item =>
        item.status ===
          racehorse360.StableEntryStatus.STABLE_ENTRY_STATUS_APPROVED ||
        item.status ===
          racehorse360.StableEntryStatus.STABLE_ENTRY_STATUS_DENIED
    );

  const hasUnsavedChanges = !isEqual(stableEntries, state.stableEntries);

  const updateData = async () => {
    await queryClient.invalidateQueries({
      queryKey: [LIST_STALL_APPLICATION_REQUEST_KEY]
    });
    await queryClient.invalidateQueries({
      queryKey: [APPLICATION_FORM_SUMMARY_REQUEST_KEY]
    });
  };

  const closeStallApplicationDetails = () => {
    dispatchToRedux(setStallApplicationUnsavedChangesPopupState(false));
    redirectToLinkCandidate();
    onClose();
  };

  const handleCloseUnsavedChangesPopup = () => {
    dispatchToRedux(setStallApplicationUnsavedChangesPopupState(false));
    clearLinkCandidate();
  };

  const handleOpenUnsavedChangesPopup = () => {
    dispatchToRedux(setStallApplicationUnsavedChangesPopupState(true));
  };

  const handleCloseReviewFormPopup = () => {
    setIsReviewFormPopupOpen(false);
  };

  const handleOpenReviewFormPopup = useCallback(() => {
    setIsReviewFormPopupOpen(true);
  }, [setIsReviewFormPopupOpen]);

  const handleSaveAndClose = async () => {
    if (hasUnsavedChanges) {
      await saveAndCloseStallApplication({
        stallApplicationId: stallApplication.id,
        stableEntriesWithStatus:
          state.stableEntries.map<racehorse360.IStableEntryWithStatusOnly>(
            se => ({
              id: se.id,
              status: se.status
            })
          )
      });
      await updateData();
    }

    closeStallApplicationDetails();
  };

  const handleComplete = async (preferences: IPreferences) => {
    const {
      firstChoiceSleepingRoomsNumber: sleepingRoomsNumberFirstChoice,
      firstChoiceStallsNumber: stallsNumberFirstChoice,
      secondChoiceSleepingRoomsNumber: sleepingRoomsNumberSecondChoice,
      secondChoiceStallsNumber: stallsNumberSecondChoice
    } = preferences;

    await completeStallApplication({
      stallApplicationId: stallApplication.id,
      stableEntriesWithStatus:
        state.stableEntries.map<racehorse360.IStableEntryWithStatusOnly>(
          se => ({
            id: se.id,
            status: se.status
          })
        ),
      sleepingRoomsNumberFirstChoice,
      stallsNumberFirstChoice,
      stallsNumberSecondChoice,
      sleepingRoomsNumberSecondChoice
    });
    await updateData();
    closeStallApplicationDetails();
  };

  const renderUnsavedChangesContent = () => {
    return (
      <span className={classes.unsavedChangesContent}>
        <span>All application progress will be lost.</span>
        <span>
          Exit this prompt and click “Save and Close” if you wish for your
          progress to be saved.
        </span>
      </span>
    );
  };

  const renderEntries = () => {
    return state.stableEntries.map(item => (
      <StableEntry
        key={item.id}
        shouldDisplayPPs={state.shouldDisplayPPs}
        entry={item}
        dispatch={dispatch}
      />
    ));
  };

  useEffect(() => {
    if (hasUnsavedChanges) {
      lockNavigation();
    } else {
      unLockNavigation();
    }
  }, [hasUnsavedChanges]);

  useEffect(() => {
    return () => {
      unLockNavigation();
    };
  }, []);

  const isLoading = [
    isStallApplicationLoading,
    isListStableEntriesFetching,
    isStallApplicationSaving,
    isStallApplicationCompleting
  ].some(Boolean);

  return (
    <OverlayPage classes={{ paper: classes.dialogPaper }}>
      <StallApplicationHeader
        title={stallApplicationForm.title}
        deadline={stallApplicationForm.deadlineDate}
        trainerName={stallApplication?.contactFullName}
      />

      <StallApplicationControlPanel
        shouldDisplayPPs={state.shouldDisplayPPs}
        shouldDisplayApp={state.shouldDisplayApp}
        areAllEntriesApproved={areAllEntriesApproved}
        areAllEntriesDenied={areAllEntriesDenied}
        entriesNumber={stallApplication?.stableEntriesCount}
        dispatch={dispatch}
      />
      <div className={classes.contentWrapper}>
        {state.shouldDisplayApp && stallApplication && (
          <div className={classes.contentItem}>
            <StallApplicationReview
              values={getReviewValues(stallApplication)}
              stablingEntries={stableEntries}
              facilityOptions={stallApplicationForm.facilityOptions}
              fieldsTitle={STALL_APPLICATION_REVIEW_TITLE}
              column={true}
            />
          </div>
        )}
        <div className={classes.stableEntries}>{renderEntries()}</div>
      </div>

      <StallApplicationFooter
        canSubmit={areAllEntriesSelected}
        onOpenReviewForm={handleOpenReviewFormPopup}
        onSaveAndClose={handleSaveAndClose}
      />

      {isReviewFormPopupOpen && (
        <ReviewFormPopup
          onClose={handleCloseReviewFormPopup}
          onSubmit={handleComplete}
          isFormSubmitting={isStallApplicationCompleting}
          data={getReviewFormPopupData(stallApplication, state.stableEntries)}
        />
      )}

      {isRSStallApplicationUnsavedChangesPopupOpen && (
        <UnsavedChangesDialog
          open={true}
          onCancel={handleCloseUnsavedChangesPopup}
          onSave={handleSaveAndClose}
          onDiscard={closeStallApplicationDetails}
          saveButtonLabel={"Save & Close"}
          cancelButtonLabel={"Back"}
          content={renderUnsavedChangesContent()}
          disallowClosePopupOutside={true}
        />
      )}

      {isLoading && <Loader className={classes.loader} />}
    </OverlayPage>
  );
};

export default React.memo(StallApplication);
